/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
    XMarkIcon,
    ShieldCheckIcon,
    PencilSquareIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { projectService } from '../../services/api';
import { ProjectMember } from '../../types';

interface EditPermissionsModalProps {
    isOpen: boolean;
    onClose: () => void;
    projectId: string;
    member: ProjectMember;
}

const EditPermissionsModal: React.FC<EditPermissionsModalProps> = ({
    isOpen,
    onClose,
    projectId,
    member,
}) => {
    const queryClient = useQueryClient();
    const [permissions, setPermissions] = useState({
        can_create: member.can_create,
        can_read: member.can_read,
        can_update: member.can_update,
        can_delete: member.can_delete,
    });

    // Reset permissions when member changes
    useEffect(() => {
        setPermissions({
            can_create: member.can_create,
            can_read: member.can_read,
            can_update: member.can_update,
            can_delete: member.can_delete,
        });
    }, [member]);

    const updateMutation = useMutation({
        mutationFn: () =>
            projectService.updateMemberPermissions(projectId, member.user_id, permissions),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['project-members', projectId] });
            toast.success('Permissions updated successfully');
            onClose();
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.error || 'Failed to update permissions');
        },
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        updateMutation.mutate();
    };

    // Quick presets
    const setPreset = (preset: 'admin' | 'editor' | 'viewer') => {
        switch (preset) {
            case 'admin':
                setPermissions({ can_create: true, can_read: true, can_update: true, can_delete: true });
                break;
            case 'editor':
                setPermissions({ can_create: true, can_read: true, can_update: true, can_delete: false });
                break;
            case 'viewer':
                setPermissions({ can_create: false, can_read: true, can_update: false, can_delete: false });
                break;
        }
    };

    if (!isOpen) return null;

    const modalContent = (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 border border-gray-800 rounded-2xl shadow-xl max-w-md w-full">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-800">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-500/20 rounded-lg">
                            <PencilSquareIcon className="w-6 h-6 text-blue-400" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-white">Edit Permissions</h3>
                            <p className="text-sm text-gray-400">{member.user?.email}</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-600 hover:text-gray-400 hover:bg-gray-800 rounded-lg"
                    >
                        <XMarkIcon className="w-5 h-5" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-5">
                    {/* Quick Presets */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                            Quick Presets
                        </label>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => setPreset('viewer')}
                                className="px-3 py-1.5 text-sm font-medium text-gray-300 rounded-lg border border-gray-700 hover:bg-gray-800 transition-colors"
                            >
                                Viewer
                            </button>
                            <button
                                type="button"
                                onClick={() => setPreset('editor')}
                                className="px-3 py-1.5 text-sm font-medium text-gray-300 rounded-lg border border-gray-700 hover:bg-gray-800 transition-colors"
                            >
                                Editor
                            </button>
                            <button
                                type="button"
                                onClick={() => setPreset('admin')}
                                className="px-3 py-1.5 text-sm font-medium text-gray-300 rounded-lg border border-gray-700 hover:bg-gray-800 transition-colors"
                            >
                                Admin
                            </button>
                        </div>
                    </div>

                    {/* Permission Checkboxes */}
                    <div className="p-4 bg-gray-800 rounded-lg space-y-3">
                        <div className="flex items-center gap-2 mb-3">
                            <ShieldCheckIcon className="w-5 h-5 text-gray-400" />
                            <p className="text-sm font-medium text-gray-300">Permissions</p>
                        </div>
                        <div className="space-y-3">
                            {[
                                { key: 'can_create', label: 'Create', desc: 'Create new pods, buckets, volumes, and other resources' },
                                { key: 'can_read', label: 'Read', desc: 'View resources and access project details' },
                                { key: 'can_update', label: 'Update', desc: 'Modify existing resources, start/stop pods' },
                                { key: 'can_delete', label: 'Delete', desc: 'Delete resources from the project' },
                            ].map((perm) => (
                                <label
                                    key={perm.key}
                                    className="flex items-start gap-3 p-3 rounded-lg bg-gray-900 border border-gray-700 hover:border-primary-500/50 cursor-pointer transition-colors"
                                >
                                    <input
                                        type="checkbox"
                                        checked={permissions[perm.key as keyof typeof permissions]}
                                        onChange={(e) =>
                                            setPermissions((prev) => ({
                                                ...prev,
                                                [perm.key]: e.target.checked,
                                            }))
                                        }
                                        className="mt-0.5 w-4 h-4 text-primary-500 rounded focus:ring-primary-500 bg-gray-800 border-gray-600"
                                    />
                                    <div className="flex-1">
                                        <p className="text-sm font-medium text-white">{perm.label}</p>
                                        <p className="text-xs text-gray-400">{perm.desc}</p>
                                    </div>
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Role Preview */}
                    <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                        <p className="text-sm text-blue-400">
                            <span className="font-medium">Role after save: </span>
                            {permissions.can_create && permissions.can_read && permissions.can_update && permissions.can_delete
                                ? 'Project Admin'
                                : 'Project Member'}
                        </p>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 btn-secondary"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={updateMutation.isPending}
                            className="flex-1 btn-primary disabled:opacity-50"
                        >
                            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
};

export default EditPermissionsModal;
